import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Menu, X, Sun, Moon } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_fintech-flow-18/artifacts/0ej5c54m_digi.png";

const links = [
  { label: "Journey", href: "#journey", type: "anchor" },
  { label: "Services", href: "/services", type: "route" },
  { label: "Why Us", href: "#why", type: "anchor" },
  { label: "FAQ", href: "#faq", type: "anchor" },
];

export default function Header({ onCtaClick }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [theme, setTheme] = useState(
    typeof window !== "undefined"
      ? localStorage.getItem("do_theme") || "light"
      : "light"
  );

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "light") root.setAttribute("data-theme", "light");
    else root.removeAttribute("data-theme");
    try { localStorage.setItem("do_theme", theme); } catch (_) {}
  }, [theme]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 18);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toggleTheme = () => setTheme((t) => (t === "light" ? "dark" : "light"));

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-strong" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <a href="#top" data-testid="brand-logo" className="flex items-center gap-3 group">
          <img
            src={LOGO}
            alt="Digital Onboarding — DigIndia PayTech"
            className="h-9 w-auto object-contain"
            style={{ filter: "drop-shadow(0 0 10px rgba(139,92,246,0.35))" }}
          />
          <span className="hidden sm:block font-display font-bold text-base tracking-tight leading-tight">
            Digital <span className="text-gradient">Onboarding</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {links.map((l) =>
            l.type === "route" ? (
              <Link
                key={l.href}
                to={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm text-slate-300 hover:text-white transition-colors"
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm text-slate-300 hover:text-white transition-colors"
              >
                {l.label}
              </a>
            )
          )}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <button
            data-testid="theme-toggle"
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className="theme-toggle"
          >
            <Sun className="sun w-4 h-4" />
            <Moon className="moon w-4 h-4" />
          </button>
          <button
            data-testid="header-cta-button"
            onClick={onCtaClick}
            className="relative px-5 py-2 rounded-full btn-primary-grad text-white text-sm font-semibold transition-all hover:-translate-y-0.5 shadow-lg shadow-violet-500/30"
          >
            Get Started
          </button>
        </div>

        <div className="md:hidden flex items-center gap-2">
          <button
            data-testid="theme-toggle-mobile"
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className="theme-toggle"
          >
            <Sun className="sun w-4 h-4" />
            <Moon className="moon w-4 h-4" />
          </button>
          <button
            className="p-2 rounded-lg glass"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden glass-strong border-t border-white/5"
            data-testid="mobile-menu"
          >
            <div className="px-5 py-4 flex flex-col gap-4">
              {links.map((l) =>
                l.type === "route" ? (
                  <Link
                    key={l.href}
                    to={l.href}
                    onClick={() => setOpen(false)}
                    className="text-base text-slate-200"
                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {l.label}
                  </Link>
                ) : (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    className="text-base text-slate-200"
                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {l.label}
                  </a>
                )
              )}
              <button
                data-testid="mobile-cta-button"
                onClick={() => { setOpen(false); onCtaClick?.(); }}
                className="mt-2 px-4 py-2.5 rounded-full btn-primary-grad text-white font-semibold"
              >
                Get Started
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
